import React from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import * as yup from 'yup';
import { LineType, SettingsData } from '../types/interfaces';
import { GET_SETTINGS } from '../graphql/query';

export const emptyTaskLine = (lineType: LineType) => ({
  name: '',
  description: '',
  lineType,
  duration: 0,
  amount: 0,
});

export const taskLineYupSchema = yup.object().shape({
  name: yup.string().required('Task name is required'),
  description: yup.string().required('Description is required'),
  lineType: yup.string().oneOf(['HOURLY', 'FIXED']).required(),
  duration: yup.number().when('lineType', {
    is: 'HOURLY',
    then: (s) => s.required('Duration is required').positive('Must be > 0'),
    otherwise: (s) => s.notRequired(),
  }),
  amount: yup.number().when('lineType', {
    is: 'FIXED',
    then: (s) => s.required('Amount is required').positive('Must be > 0'),
    otherwise: (s) => s.notRequired(),
  }),
});

export const useDefaultLineType = (): LineType => {
  const { data } = useQuery<SettingsData>(GET_SETTINGS);
  return data?.settings?.defaultBillingMode ?? 'HOURLY';
};

interface TaskLineTypeFieldsProps {
  index: number;
  lineType: LineType;
  duration: number;
  amount: number;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  touched?: any;
  errors?: any;
}

const TaskLineTypeFields: React.FC<TaskLineTypeFieldsProps> = ({
  index,
  lineType,
  duration,
  amount,
  onChange,
  touched,
  errors,
}) => (
  <>
    <Grid size={6}>
      <TextField
        select
        fullWidth
        id={`tasks[${index}].lineType`}
        name={`tasks[${index}].lineType`}
        label="Billing type"
        value={lineType}
        onChange={onChange}
      >
        <MenuItem value="HOURLY">Hourly</MenuItem>
        <MenuItem value="FIXED">Fixed price</MenuItem>
      </TextField>
    </Grid>
    <Grid size={6}>
      {lineType === 'FIXED' ? (
        <TextField
          fullWidth
          type="number"
          id={`tasks[${index}].amount`}
          name={`tasks[${index}].amount`}
          label="Amount ($)"
          value={amount}
          onChange={onChange}
          error={touched?.amount && Boolean(errors?.amount)}
          helperText={touched?.amount && errors?.amount}
        />
      ) : (
        <TextField
          fullWidth
          type="number"
          id={`tasks[${index}].duration`}
          name={`tasks[${index}].duration`}
          label="Duration (hours)"
          value={duration}
          onChange={onChange}
          error={touched?.duration && Boolean(errors?.duration)}
          helperText={touched?.duration && errors?.duration}
        />
      )}
    </Grid>
  </>
);

export default TaskLineTypeFields;
